import React, { useState } from 'react';
import { KeyIcon, ServerIcon, CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { connectionsAPI } from '../../../api/enhancedApiService';
import { useUI } from '../../../contexts/UIContext';

export default function SnowflakeKeyPairForm({ onConnectionCreated, onCancel }) {
  const { showNotification } = useUI();
  const [loading, setLoading] = useState(false);
  const [keyPair, setKeyPair] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [generatingKeys, setGeneratingKeys] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [formData, setFormData] = useState({
    connection_name: '',
    account: '',
    username: '',
    private_key: '',
    warehouse: 'COMPUTE_WH',
    database: 'SPARVI',
    schema: 'SANDBOX'
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Simple success indication without notification library
      console.log('Copied to clipboard');
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateKeyPair = async () => {
    try {
      setGeneratingKeys(true);
      const response = await connectionsAPI.generateKeyPair();

      if (response.private_key && response.public_key) {
        setKeyPair(response);
        setFormData(prev => ({
          ...prev,
          private_key: response.private_key
        }));
        setCurrentStep(1);
        setShowInstructions(true);

        // Show notification if this is an existing key being reused
        if (response.existing) {
          showNotification(
            'Reusing existing organization key pair - already configured in Snowflake for all connections. No need to run ALTER USER again unless you previously removed this key.',
            'warning',
            8000
          );
        }
      }
    } catch (error) {
      console.error('Failed to generate key pair:', error);
    } finally {
      setGeneratingKeys(false);
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);
      setTestResult(null);

      const response = await connectionsAPI.testKeyPairConnection({
        account: formData.account,
        username: formData.username,
        private_key: formData.private_key,
        warehouse: formData.warehouse,
        database: formData.database,
        schema: formData.schema
      });

      if (response.success) {
        setTestResult({ success: true, message: response.message });
        setCurrentStep(2);
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTestingConnection(false);
    }
  };

  const createConnection = async () => {
    try {
      setLoading(true);

      // Validate required fields
      const requiredFields = ['connection_name', 'account', 'username', 'private_key'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);
        showNotification(`Please fill in the following required fields: ${missingFields.join(', ')}`, 'error', 5000);
        return;
      }

      const response = await connectionsAPI.createKeyPairConnection(formData);
      
      console.log('Connection creation response:', response);

      if (response.success) {
        onConnectionCreated?.(response.connection_id);
      } else {
        console.error('Connection creation failed:', response);
        showNotification('Failed to create connection: ' + (response.error || 'Unknown error'), 'error', 5000);
      }
    } catch (error) {
      console.error('Error creating connection:', error);
      showNotification('Failed to create connection: ' + (error.response?.data?.error || error.message || 'Unknown error'), 'error', 5000);
    } finally {
      setLoading(false);
    }
  };

  const publicKeyForSnowflake = keyPair ? 
    keyPair.public_key
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\n/g, '') 
    : '';

  const snowflakeCommand = formData.username && publicKeyForSnowflake
    ? `ALTER USER ${formData.username.toUpperCase()} SET RSA_PUBLIC_KEY='${publicKeyForSnowflake}';`
    : '';

  const copySnowflakeCommand = () => {
    if (snowflakeCommand) {
      navigator.clipboard.writeText(snowflakeCommand).then(() => {
        setCopiedCommand(true);
        setTimeout(() => setCopiedCommand(false), 2000);
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <KeyIcon className="h-6 w-6 text-blue-600" />
        <h3 className="text-lg font-medium text-gray-900">Snowflake Key Pair Authentication</h3>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Secure Service Account Authentication:</strong> Key pair authentication provides secure, 
          password-free access to Snowflake using public/private key cryptography. This method is ideal 
          for production environments and automated workflows.
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Enter Username and Generate Key Pair */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-2">Step 1: Enter Username & Get Organization Key Pair</h4>
          <p className="text-sm text-gray-600 mb-3">
            Your organization uses one shared key pair for all Snowflake connections to avoid conflicts.
          </p>
          
          <div className="mb-4">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Snowflake Username *
            </label>
            <input
              type="text"
              name="username"
              id="username"
              required
              value={formData.username}
              onChange={handleInputChange}
              placeholder="SPARVI_SERVICE_ACCOUNT"
              className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            />
            <p className="mt-1 text-sm text-gray-500">
              Enter your Snowflake username to generate the setup command automatically.
            </p>
          </div>
          
          <button
            type="button"
            onClick={generateKeyPair}
            disabled={!!keyPair || generatingKeys || !formData.username}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <KeyIcon className="-ml-1 mr-2 h-5 w-5" />
            {generatingKeys ? 'Generating...' : keyPair ? 'Key Pair Generated' : 'Generate New Key Pair'}
          </button>
        </div>

        {/* Step 2: Setup Instructions */}
        {keyPair && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-2">Step 2: Configure Snowflake User</h4>
            <p className="text-sm text-gray-600 mb-4">
              Copy the public key below and run this command in Snowflake as ACCOUNTADMIN:
            </p>
            <div className="relative">
              <div className="block w-full text-xs font-mono border border-gray-300 rounded-md bg-gray-800 text-green-400 p-3 pr-10 overflow-x-auto">
                {snowflakeCommand || 'Enter username above to generate command'}
              </div>
              {snowflakeCommand && (
                <button
                  type="button"
                  onClick={copySnowflakeCommand}
                  className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white transition-colors"
                  title="Copy command"
                >
                  {copiedCommand ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  ) : (
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  )}
                </button>
              )}
            </div>
            {copiedCommand && (
              <p className="text-xs text-green-600 mt-2">Command copied to clipboard!</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              This command sets the public key for user: <strong>{formData.username.toUpperCase()}</strong>
            </p>
            
            <button
              type="button"
              onClick={() => setShowInstructions(!showInstructions)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800"
            >
              {showInstructions ? 'Hide' : 'Show'} Raw Public Key
            </button>
            
            {showInstructions && (
              <textarea
                readOnly
                value={keyPair.public_key}
                rows={8}
                className="mt-2 block w-full text-xs font-mono border border-gray-300 rounded-md bg-white p-2"
              />
            )}
          </div>
        )}

        {/* Step 3: Connection Details */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900">Step 3: Connection Details</h4>
          
          <div>
            <label htmlFor="connection_name" className="block text-sm font-medium text-gray-700">
              Connection Name *
            </label>
            <input
              type="text"
              name="connection_name"
              id="connection_name"
              required
              value={formData.connection_name}
              onChange={handleInputChange}
              placeholder="My Snowflake Connection"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label htmlFor="account" className="block text-sm font-medium text-gray-700">
              Account Identifier *
            </label>
            <input
              type="text"
              name="account"
              id="account"
              required
              value={formData.account}
              onChange={handleInputChange}
              placeholder="xy12345.us-east-1"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            />
            <p className="mt-1 text-sm text-gray-500">Your Snowflake account identifier (e.g., xy12345.us-east-1)</p>
          </div>


          <div>
            <label htmlFor="private_key" className="block text-sm font-medium text-gray-700">
              Private Key *
            </label>
            <textarea
              name="private_key"
              id="private_key"
              required
              value={formData.private_key}
              onChange={handleInputChange}
              rows={8}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md font-mono text-xs"
            />
            <p className="mt-1 text-sm text-gray-500">The private key will be securely encrypted before storage</p>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showAdvanced ? 'Hide' : 'Show'} Optional Settings
          </button>

          {showAdvanced && (
            <div className="space-y-4 pl-4 border-l-2 border-gray-200">
              <div>
                <label htmlFor="warehouse" className="block text-sm font-medium text-gray-700">
                  Warehouse
                </label>
                <input
                  type="text"
                  name="warehouse"
                  id="warehouse"
                  value={formData.warehouse}
                  onChange={handleInputChange}
                  placeholder="COMPUTE_WH"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">Default warehouse to use for queries (your account has access to COMPUTE_WH)</p>
              </div>

              <div>
                <label htmlFor="database" className="block text-sm font-medium text-gray-700">
                  Database
                </label>
                <input
                  type="text"
                  name="database"
                  id="database"
                  value={formData.database}
                  onChange={handleInputChange}
                  placeholder="SPARVI"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">Default database to connect to (your account has access to SPARVI)</p>
              </div>

              <div>
                <label htmlFor="schema" className="block text-sm font-medium text-gray-700">
                  Schema
                </label>
                <input
                  type="text"
                  name="schema"
                  id="schema"
                  value={formData.schema}
                  onChange={handleInputChange}
                  placeholder="SANDBOX"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">Default schema to use (your account has access to SPARVI.SANDBOX)</p>
              </div>
            </div>
          )}
        </div>

        {/* Step 4: Test Connection */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-md font-medium text-gray-900 mb-2">Step 4: Test Connection</h4>
          <button
            type="button"
            onClick={testConnection}
            disabled={!keyPair || testingConnection}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <ServerIcon className="-ml-1 mr-2 h-5 w-5" />
            {testingConnection ? 'Testing...' : 'Test Connection'}
          </button>
          
          {testResult && (
            <div className={`mt-4 p-4 rounded-md ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex">
                <CheckCircleIcon className={`h-5 w-5 ${testResult.success ? 'text-green-400' : 'text-red-400'}`} />
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </h3>
                  <p className={`mt-1 text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.message}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={createConnection}
            disabled={!testResult?.success || loading}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" />
            {loading ? 'Creating...' : 'Create Connection'}
          </button>
        </div>
      </div>
    </div>
  );
}